import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../auth/Auth';
import { useModal } from '../../helpers/hooks/modalContext';
import { usePoints } from '../../helpers/hooks/pointsContext';
import { Spinner } from '../../helpers/UI/Spinner/Spinner';
import { RedeemHero } from './components/RedeemHero';
import { RedeemStatsSection } from './components/RedeemStatsSection';
import { RedeemRentCreditsSection } from './components/RedeemRentCreditsSection';
import { GiftCardsSection } from './components/GiftCardsSection';
import { WalletHistorySection } from './components/WalletHistorySection';
import { RentCreditHistorySection } from './components/RentCreditHistorySection';
import { useWalletRentCredits } from './hooks/useWalletRentCredits';
import { useWalletGiftCards } from './hooks/useWalletGiftCards';
import { useWalletHistory } from './hooks/useWalletHistory';
import { useRentCreditHistory } from './hooks/useRentCreditHistory';
import { useRedeemStreak } from './hooks/useRedeemStreak';
import { scrollToRedeemSection } from './utils/redeemUtils';
import './RedeemPage.css';

const sectionVariant = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const WalletPage: React.FC = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { setModal } = useModal();
  const { points, loading, refreshPoints } = usePoints();
  const [giftSearch, setGiftSearch] = useState('');
  const [pageOn, setPageOn] = useState(false);

  const totalPointsNum = Number(points?.data?.points?.totalAvailable ?? 0);
  const totalTicketsNum = Number(points?.data?.tickets?.totalAvailable ?? 0);
  const token = auth?.data.token;

  const {
    loading: rentCreditsLoading,
    options: rentCreditOptions,
    redeemingId,
    redeem,
  } = useWalletRentCredits(token);
  const { cards: giftCards, loading: giftCardsLoading } = useWalletGiftCards({
    token,
    search: giftSearch,
    page: 1,
    limit: 48,
    debounceMs: 350,
  });
  const { items: walletHistory, loading: walletHistoryLoading } = useWalletHistory(token);
  const {
    items: rentCreditHistory,
    stats: rentCreditStats,
    loading: rentCreditHistoryLoading,
    refetch: refetchRentCreditHistory,
  } = useRentCreditHistory(token);
  const { streak } = useRedeemStreak(token);

  useEffect(() => {
    document.body.classList.add('page-redeem');
    const frame = requestAnimationFrame(() => setPageOn(true));
    return () => {
      cancelAnimationFrame(frame);
      document.body.classList.remove('page-redeem');
    };
  }, []);

  useEffect(() => {
    if (window.location.hash !== '#rd-rent-savings') {
      return;
    }

    const scrollTimer = window.setTimeout(() => {
      scrollToRedeemSection('rd-rent-savings');
    }, 450);

    return () => {
      window.clearTimeout(scrollTimer);
    };
  }, []);

  const scrollToRent = () => scrollToRedeemSection('rd-rent-savings');
  const scrollToGiftCards = () => scrollToRedeemSection('rd-gift-cards');
  const goEarn = () => navigate('/rewards/earn');
  const openEVoucher = () =>
    setModal('e-voucher', { onEVoucherRedeemed: refetchRentCreditHistory });

  if (loading) return <Spinner />;

  return (
    <div className='text-main'>
      <motion.div
        className={`pg${pageOn ? ' on' : ''}`}
        id='pg-redeem'
        initial='hidden'
        animate='show'
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.12, delayChildren: 0.02 } },
        }}
      >
        <motion.div variants={sectionVariant}>
          <RedeemHero
            points={totalPointsNum}
            tickets={totalTicketsNum}
            onScrollToRent={scrollToRent}
            onScrollToGiftCards={scrollToGiftCards}
            onGoEarn={goEarn}
            onEVoucher={openEVoucher}
          />
        </motion.div>

        <div className='rd-content'>
          <motion.div variants={sectionVariant}>
            <RedeemStatsSection
              points={totalPointsNum}
              tickets={totalTicketsNum}
              streak={streak}
              onScrollToRent={scrollToRent}
              onScrollToGiftCards={scrollToGiftCards}
              onGoEarn={goEarn}
            />
          </motion.div>

          <motion.div variants={sectionVariant}>
            <RedeemRentCreditsSection
              loading={rentCreditsLoading}
              options={rentCreditOptions}
              pointsAvailable={totalPointsNum}
              redeemingId={redeemingId}
              onRedeem={async (option) => {
                await redeem(option, refreshPoints);
              }}
            />
          </motion.div>

          <motion.div variants={sectionVariant} className='rd-bottom'>
            <GiftCardsSection
              search={giftSearch}
              onSearchChange={setGiftSearch}
              loading={giftCardsLoading}
              cards={giftCards}
            />
            <div className='rd-history-stack'>
              <WalletHistorySection loading={walletHistoryLoading} items={walletHistory} />
              <RentCreditHistorySection
                loading={rentCreditHistoryLoading}
                items={rentCreditHistory}
                stats={rentCreditStats}
              />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default WalletPage;
